import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseBadge from "@/components_v2/Badge/BaseBadge";
import BaseAvatar from "@/components_v2/Avatar/BaseAvatar";
import {
  ApplicationStatuses,
  OrganizationRoles,
} from "@/shared/utils/constants";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import ApplicationAcceptModal from "@/features/organizations/profile/components/ApplicationAcceptModal";
import ApplicationRejectModal from "@/features/organizations/profile/components/ApplicationRejectModal";
import {
  ApplicationDetailFieldProps,
  ApplicationDetailModalProps,
} from "@/features/organizations/profile/types";

import "../styles.css";

const ApplicationDetailField = ({
  label,
  children,
  className,
}: ApplicationDetailFieldProps) => (
  <div className={`flex flex-col gap-100 ${className ?? ""}`}>
    <span className="application-detail-modal__label body-xs-regular">
      {label}
    </span>
    <div className="application-detail-modal__value body-m-regular">
      {children}
    </div>
  </div>
);

const ApplicationDetailModal = ({
  open,
  setOpen,
  application,
  onUpdated,
}: ApplicationDetailModalProps) => {
  const { t } = useTranslation();

  const [openAcceptModal, setOpenAcceptModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);

  const onClose = () => setOpen(false);

  const status = ApplicationStatuses.getItemByValue(application.status);
  const isPending = application.status === ApplicationStatuses.PENDING.value;
  const isAccepted =
    application.status === ApplicationStatuses.ACCEPTED.value ||
    application.status === ApplicationStatuses.REGISTERED.value;

  const appliedAt = DateTime.fromISO(application.createdAt);
  const daysWaiting = Math.max(
    Math.floor(DateTime.now().diff(appliedAt, "days").days),
    0,
  );

  return (
    <>
      <BaseModal
        destroyOnClose
        className="application-detail-modal"
        title={t("organizations.labels.applicationDetails")}
        open={open}
        onCancel={onClose}
        footer={
          <div className="modal-footer-buttons">
            <BaseButton
              size={BUTTON_SIZES.SMALL}
              variant={BUTTON_VARIANTS.TERTIARY}
              onClick={onClose}
            >
              {t("organizations.labels.close")}
            </BaseButton>

            {isPending && (
              <div className="flex gap-200">
                <BaseButton
                  className="application-detail-modal__reject-button"
                  variant={BUTTON_VARIANTS.DANGER}
                  size={BUTTON_SIZES.SMALL}
                  onClick={() => setOpenRejectModal(true)}
                >
                  {t("organizations.labels.reject")}
                </BaseButton>

                <BaseButton
                  variant={BUTTON_VARIANTS.PRIMARY}
                  size={BUTTON_SIZES.SMALL}
                  onClick={() => setOpenAcceptModal(true)}
                >
                  {t("organizations.labels.acceptApplication")}
                </BaseButton>
              </div>
            )}
          </div>
        }
      >
        <div className="flex flex-col gap-400">
          <div className="application-detail-modal__applicant flex items-center gap-300">
            <BaseAvatar
              size="extra-large"
              label={`${application.firstName.charAt(0)}${application.lastName.charAt(0)}`}
            />

            <div className="application-detail-modal__applicant-info flex flex-col">
              <span className="application-detail-modal__name body-m-medium one-line-ellipsis">
                {application.firstName} {application.lastName}
              </span>

              <span className="application-detail-modal__email body-s-regular one-line-ellipsis">
                {application.email}
              </span>
            </div>

            <BaseBadge
              className="application-detail-modal__status"
              leadingIcon={
                <span
                  className="application-detail-modal__status-dot"
                  style={{ backgroundColor: status?.color }}
                />
              }
              backgroundColor={status?.backgroundColor}
              fontColor={status?.color}
              borderColor={status?.backgroundColor}
            >
              {status?.label || application.status}
            </BaseBadge>
          </div>

          <div className="application-detail-modal__grid">
            <ApplicationDetailField label={t("organizations.labels.applied")}>
              {appliedAt.toFormat("dd MMMM yyyy")}
            </ApplicationDetailField>

            {isPending && (
              <ApplicationDetailField label={t("organizations.labels.waiting")}>
                {t("organizations.labels.daysWaiting", { count: daysWaiting })}
              </ApplicationDetailField>
            )}

            {isAccepted && (
              <ApplicationDetailField
                label={t("organizations.labels.assignedRole")}
              >
                {OrganizationRoles.getItemByValue(application.assignedRole)
                  ?.label || application.assignedRole}
              </ApplicationDetailField>
            )}
          </div>

          {application.note && (
            <ApplicationDetailField
              label={t("organizations.labels.noteFromApplicant")}
            >
              <p className="application-detail-modal__box">
                {application.note}
              </p>
            </ApplicationDetailField>
          )}

          {application.status === ApplicationStatuses.REJECTED.value &&
            application.rejectionReason && (
              <ApplicationDetailField
                label={t("organizations.labels.rejectionReason")}
              >
                <p className="application-detail-modal__box">
                  {application.rejectionReason}
                </p>
              </ApplicationDetailField>
            )}
        </div>
      </BaseModal>

      <ApplicationAcceptModal
        open={openAcceptModal}
        setOpen={setOpenAcceptModal}
        application={application}
        onAccepted={() => {
          onClose();
          onUpdated();
        }}
      />

      <ApplicationRejectModal
        open={openRejectModal}
        setOpen={setOpenRejectModal}
        application={application}
        onRejected={() => {
          onClose();
          onUpdated();
        }}
      />
    </>
  );
};

export default ApplicationDetailModal;
